export const ROOM_TYPES = {
  LEGACY: 'legacy',
  ADULT_PRIVATE: 'adult-private',
  FAMILY: 'family',
  SCHOOL: 'school',
  SCHOOL_CLASSMATE: 'school-classmate',
  SCHOOL_STAFF: 'school-staff'
} as const;

export type RoomType = typeof ROOM_TYPES[keyof typeof ROOM_TYPES];

const ROOM_TYPE_VALUES = new Set<string>(Object.values(ROOM_TYPES));

export function canonicalizeRoomType(value: unknown): RoomType {
  if (
    value === ROOM_TYPES.ADULT_PRIVATE
    || value === ROOM_TYPES.FAMILY
    || value === ROOM_TYPES.SCHOOL
    || value === ROOM_TYPES.SCHOOL_CLASSMATE
    || value === ROOM_TYPES.SCHOOL_STAFF
  ) {
    return value;
  }
  return ROOM_TYPES.FAMILY;
}

export function normalizeRoomType(value: unknown): RoomType {
  if (typeof value === 'string' && ROOM_TYPE_VALUES.has(value)) {
    return canonicalizeRoomType(value);
  }
  return ROOM_TYPES.FAMILY;
}

export function roomTypeLabel(value: unknown): string {
  switch (normalizeRoomType(value)) {
    case ROOM_TYPES.ADULT_PRIVATE:
      return 'Encrypted';
    case ROOM_TYPES.SCHOOL:
      return 'School';
    case ROOM_TYPES.SCHOOL_CLASSMATE:
      return 'Classmate';
    case ROOM_TYPES.SCHOOL_STAFF:
      return 'School staff';
    default:
      return 'Family';
  }
}

export function roomTypeRequiresEncryption(value: unknown): boolean {
  const roomType = normalizeRoomType(value);
  return roomType === ROOM_TYPES.ADULT_PRIVATE || roomType === ROOM_TYPES.SCHOOL_CLASSMATE;
}

export function effectiveRoomEncryption(value: unknown, requested: boolean): boolean {
  return roomTypeRequiresEncryption(value) || requested;
}

export function allowThreadInvites(value: unknown): boolean {
  const roomType = normalizeRoomType(value);
  return roomType !== ROOM_TYPES.SCHOOL_CLASSMATE
    && roomType !== ROOM_TYPES.SCHOOL_STAFF
    && roomType !== ROOM_TYPES.SCHOOL;
}

export function roomSecurityLabel(
  thread: { roomType?: unknown; encryptionEnabled?: unknown } | null | undefined,
  encryptionLocked?: boolean
): string {
  const type = roomTypeLabel(thread?.roomType);
  if (!thread?.encryptionEnabled) return `${type} · 🔓 Not encrypted`;
  if (encryptionLocked === true) return `${type} · 🔒 Locked`;
  if (encryptionLocked === false) return `${type} · 🔓 Encrypted (unlocked)`;
  return `${type} · 🔒 Encrypted`;
}
